import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import {
  EFeatureFlag,
  FeatureFlags,
  GetBootResponse,
  CheckoutScreenTheme,
  CouponsConfiguration,
  PublisherData,
} from '../contracts';
import { initI18n } from '../i18n';
import { API_V1 } from '../services/api.service';
import { getHostDomain } from '../utils/get-host-domain';
import { getSessionToken } from '../utils/get-session-token';
import { localStorageUtil } from '../utils/local-storage-util';
import { getCheckoutToken } from '../utils/get-checkout-token';
import { initProductEventsService } from '../utils/mixpanel-utils';
import { getBootLocalStorageKeyBySuffix } from '../utils/get-boot-local-storage-key';

export interface AppConfig {
  theme: Theme;
  publisherId: string;
  supportEmail: string;
  featureFlags?: FeatureFlags;
  couponsConfiguration: CouponsConfiguration;
  isDefaultBoot: boolean;
  isBootInitialized: boolean;
  publisherData: PublisherData;
}

interface Theme {
  logo: string;
  backgroundImage?: string;
  mobileBackgroundImage?: string;
  button: {
    color: string;
    textColor: string;
  };
  text: string;
  primary: string;
}

export const AppConfigContext = createContext<Partial<AppConfig> | null>(null);

const appThemeAdaptar = (checkoutTheme: CheckoutScreenTheme): Theme => {
  if (!checkoutTheme) {
    return defaultTheme;
  }
  return {
    logo: checkoutTheme.logo,
    button: { color: checkoutTheme.buttonColor, textColor: checkoutTheme.buttonTextColor },
    primary: checkoutTheme.primaryColor,
    text: checkoutTheme.textColor,
    backgroundImage: checkoutTheme.backgroundImage,
    mobileBackgroundImage: checkoutTheme.mobileBgImage || checkoutTheme.backgroundImage,
  };
};

const getInitialBootFromLocalStorage = () => {
  const checkoutToken = getCheckoutToken();
  const bootLocalStorageKeyByCheckoutToken = getBootLocalStorageKeyBySuffix(checkoutToken);
  const bootByCheckoutToken = localStorageUtil.get(bootLocalStorageKeyByCheckoutToken);
  if (bootLocalStorageKeyByCheckoutToken && bootByCheckoutToken) {
    return bootByCheckoutToken;
  }
  const domain = getHostDomain();
  const bootLocalStorageKeyByDomain = getBootLocalStorageKeyBySuffix(domain);
  const bootByDomain = localStorageUtil.get(bootLocalStorageKeyByDomain);
  return bootByDomain;
};

const getInitialConfig = () => {
  const currentBoot = getInitialBootFromLocalStorage() as GetBootResponse;
  if (currentBoot) {
    const appTheme = appThemeAdaptar(currentBoot?.checkoutTheme);
    return {
      theme: appTheme,
      publisherId: currentBoot.checkoutTheme.publisherId,
      supportEmail: currentBoot.supportEmail,
      couponsConfiguration: currentBoot.couponsConfiguration,
      featureFlags: currentBoot.featureFlags,
      isDefaultBoot: false,
      publisherData: {
        companyId: currentBoot.publisherData.companyId,
        companyName: currentBoot.publisherData.companyName,
        storeTabName: currentBoot.publisherData.storeTabName,
        id: currentBoot.publisherData.id,
      },
    };
  }

  return { theme: defaultTheme, isDefaultBoot: true };
};

const defaultTheme = {
  button: {
    color: '#7122FF',
    textColor: '#FFF',
  },
  text: '#000',
  primary: '#CACBD4',
  logo: '',
  backgroundImage: '',
  mobileBackgroundImage: '',
};

const defaultFeatureFlags: FeatureFlags = {
  [EFeatureFlag.CHECKOUT_DISABLE_SUCCESS_SCREEN]: true,
};

export const AppConfigProvider = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState<Partial<AppConfig>>(getInitialConfig);
  const [isBootInitialized, setIsBootInitialized] = useState(!config.isDefaultBoot);
  const urlParams = new URLSearchParams(window.location.search);
  const cot = urlParams.get('cot');

  useEffect(() => {
    initProductEventsService();
  }, []);

  useEffect(() => {
    const sessionToken = getSessionToken();
    if (sessionToken) {
      initI18n(sessionToken);
    }
  }, []);

  const fetchFallbackBoot = useCallback(() => {
    const domain = cot ? '' : getHostDomain();
    const checkoutToken = cot || getCheckoutToken();

    API_V1.getBoot(domain, checkoutToken)
      .then((data) => {
        const appTheme = appThemeAdaptar(data?.checkoutTheme);
        const supportEmail = data.supportEmail;
        setConfig((prevConfig) => ({
          ...prevConfig,
          theme: appTheme,
          publisherId: data.checkoutTheme.publisherId,
          couponsConfiguration: data.couponsConfiguration,
          supportEmail,
          featureFlags: data.featureFlags,
        }));
      })
      .catch((err) => {
        console.error(err);
        setConfig((prevConfig) => ({
          ...prevConfig,
          featureFlags: defaultFeatureFlags,
        }));
      })
      .finally(() => setIsBootInitialized(true));
  }, []);

  useEffect(() => {
    if (!isBootInitialized) {
      fetchFallbackBoot();
    }
  }, [isBootInitialized, fetchFallbackBoot]);

  return (
    <AppConfigContext.Provider value={{ ...config, isBootInitialized }}>
      {children}
    </AppConfigContext.Provider>
  );
};
